import CustomTable from "@/components/CustomTable";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchIcon } from "@/assets";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getExternalRates } from "@/services/Rates.service";
import { externalRateColumns, type ExternalRate } from "./columns";

const FALLBACK_PROVIDER = "N/A";

const parseRateValue = (
  value: number | string | undefined | null
): number | null => {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const ExternalRates = () => {
  const [search, setSearch] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState<string>("all");
  const [providerFilter, setProviderFilter] = useState<string>("all");

  const externalRatesQuery = useQuery({
    queryKey: ["externalRates"],
    queryFn: getExternalRates,
  });

  const normalizedRates = useMemo<ExternalRate[]>(() => {
    const payload = externalRatesQuery.data?.data?.data;
    if (!Array.isArray(payload)) {
      return [];
    }
    return payload.map((rate) => ({
      id: rate.id,
      currency: `${rate.base}/${rate.quote}`,
      buyingRate: parseRateValue(rate.buyRate),
      sellingRate: parseRateValue(rate.sellRate),
      buyRateProvider: rate.buyRateProvider ?? FALLBACK_PROVIDER,
      sellRateProvider: rate.sellRateProvider ?? FALLBACK_PROVIDER,
    }));
  }, [externalRatesQuery.data]);

  const currencyOptions = useMemo(() => {
    const options = new Set<string>();
    normalizedRates.forEach((row) => {
      const [base, quote] = row.currency.split("/");
      if (base && quote) {
        options.add(base);
      }
    });
    return Array.from(options);
  }, [normalizedRates]);

  const providers = useMemo(() => {
    const options = new Set<string>();
    normalizedRates.forEach((row) => {
      if (row.buyRateProvider && row.buyRateProvider !== FALLBACK_PROVIDER) {
        options.add(row.buyRateProvider);
      }
      if (row.sellRateProvider && row.sellRateProvider !== FALLBACK_PROVIDER) {
        options.add(row.sellRateProvider);
      }
    });
    return Array.from(options);
  }, [normalizedRates]);

  useEffect(() => {
    if (
      currencyFilter !== "all" &&
      !currencyOptions.includes(currencyFilter)
    ) {
      setCurrencyFilter("all");
    }
  }, [currencyFilter, currencyOptions]);

  useEffect(() => {
    if (
      providerFilter !== "all" &&
      !providers.includes(providerFilter)
    ) {
      setProviderFilter("all");
    }
  }, [providerFilter, providers]);

  const filteredData = useMemo(() => {
    return normalizedRates.filter((row) => {
      const matchesSearch =
        !search ||
        row.currency.toLowerCase().includes(search.toLowerCase()) ||
        (row.buyRateProvider && row.buyRateProvider.toLowerCase().includes(search.toLowerCase())) ||
        (row.sellRateProvider && row.sellRateProvider.toLowerCase().includes(search.toLowerCase()));
      const matchesCurrency =
        currencyFilter === "all" || row.currency.startsWith(currencyFilter);
      const matchesProvider =
        providerFilter === "all" ||
        (row.buyRateProvider && row.buyRateProvider === providerFilter) ||
        (row.sellRateProvider && row.sellRateProvider === providerFilter);
      return matchesSearch && matchesCurrency && matchesProvider;
    });
  }, [normalizedRates, search, currencyFilter, providerFilter]);

  return (
    <div className="grid gap-8 p-6 py-12">
      <div>
        <p className="text-2xl font-medium text-primary-3">External Rates</p>
      </div>

      <div className="rounded-[12px] border border-[#EAE6EA] bg-card shadow-sm p-6 flex flex-col gap-6">
        <h3 className="text-base font-semibold text-primary-3">
          External provider rates
        </h3>

        <div className="flex flex-wrap items-end gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-70">
              <SearchIcon />
            </span>
            <Input
              placeholder="Search pair or provider"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 border-grey-300 rounded-[8px] bg-off-white-1"
            />
          </div>

          <div className="flex items-end gap-4 flex-wrap">
            <div className="flex flex-col gap-1.5 min-w-[120px]">
              <label className="text-xs text-gray-80">Currency</label>
              <Select value={currencyFilter} onValueChange={setCurrencyFilter}>
                <SelectTrigger className="h-10 border-grey-300 rounded-[8px] px-3 w-[120px]">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {currencyOptions.map((currency) => (
                    <SelectItem key={currency} value={currency}>
                      {currency}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5 min-w-[120px]">
              <label className="text-xs text-gray-80">Provider</label>
              <Select value={providerFilter} onValueChange={setProviderFilter}>
                <SelectTrigger className="h-10 border-grey-300 rounded-[8px] px-3 w-[140px]">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {providers.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="border rounded-md overflow-hidden">
          <CustomTable
            columns={externalRateColumns}
            data={filteredData}
            isLoading={externalRatesQuery.isLoading}
          />
        </div>
        {externalRatesQuery.isError && (
          <p className="text-sm text-red-500 px-4 py-3">
            Failed to load external rates.
          </p>
        )}
      </div>
    </div>
  );
};

export default ExternalRates;
